import { KEY, NOTES_PREFIX, WINDOWS_PREFIX, yearMonthOf, yearMonthOfEpochDay } from '../../stores/keys'
import { getLong, logicalKeys, setLong, type Storage } from '../../platform/storage'
import { profileUpdatedAt, saveProfile } from '../../stores/profile'
import type { Note } from '../../stores/notes'
import { mergeEvents, type DayEvent, type EndReason, type EventKind } from '../../stores/events'
import { CHILD_ID_KEY, type SyncAnswer } from './payload'
import { needsConsent } from '../consent'
import { LANG_KEY, isLang, lang as текущийЯзык, setLang } from '../../i18n'

const ПРИЧИНЫ = new Set<string>(['asleep', 'awake', 'reset', 'unanswered'])
const ВИДЫ = new Set<EventKind>(['phase', 'window_end', 'night_wake', 'night_wake_end', 'weight'])

/**
 * Язык аккаунта с сервера. Применяется, только если на этом устройстве человек язык
 * не выбирал: его выбор здесь сильнее (план `docs/i18n-plan.md`, 4.1). Так второй
 * телефон сразу говорит на нужном языке, а первый не переучивается.
 */
async function применитьЯзыкАккаунта(answer: SyncAnswer): Promise<void> {
  const сервер = answer.locale?.lang
  if (!isLang(сервер) || сервер === текущийЯзык.value) return
  try {
    if (globalThis.localStorage?.getItem(LANG_KEY) !== null) return
  } catch {
    return
  }
  await setLang(сервер, false)
}

/** Кладёт ответ сервера в хранилище устройства. */
export async function apply(storage: Storage, answer: SyncAnswer): Promise<void> {
  await применитьЯзыкАккаунта(answer)
  // Сервер говорит, нужно ли «догоняющее» согласие (старый аккаунт или новая редакция)
  if (answer.needsConsent !== undefined) needsConsent.value = answer.needsConsent
  /**
   * Чья запись свежее — того и правда, как и на сервере.
   *
   * Без этого ответ затирал только что сделанное на устройстве: добавили окно
   * бодрствования и перезагрузили приложение раньше, чем посылка уехала, — сервер
   * «возвращал» прежнее состояние, то есть стирал новое. Нашёл дымовой прогон по
   * проду 27.10.2026; похоже, из-за этого же у человека пропадали окна дня.
   * Записи, история и заметки ниже приезжают по своим правилам и от этого не зависят.
   */
  const профильСервераСвежее = answer.child !== null && answer.child.updatedAtMs >= (await profileUpdatedAt(storage))
  if (answer.child !== null && профильСервераСвежее) {
    await saveProfile(
      storage,
      {
        name: answer.child.name,
        birthMillis: answer.child.birthMs,
        gender: answer.child.gender,
        gasVariant: answer.child.gasVariant,
        ...(typeof answer.child.birthWeightGrams === 'number' && answer.child.birthWeightGrams > 0
          ? { birthWeightGrams: answer.child.birthWeightGrams }
          : {}),
      },
      answer.child.updatedAtMs,
    )
    try {
      globalThis.localStorage?.setItem(CHILD_ID_KEY, answer.child.clientId)
    } catch {
      // приватный режим — номер останется прежним
    }
    const правки = answer.child.customPhases ?? {}
    for (const [programId, phases] of Object.entries(правки)) {
      if (Array.isArray(phases) && phases.length > 0) {
        await setLong(storage, KEY.customPhases(programId), JSON.stringify(phases))
      }
    }
    // Правка, которой у сервера больше нет, должна уйти и отсюда. Иначе «Вернуть
    // рекомендованное» на телефоне не доезжало до планшета: там правки жили вечно —
    // ровно та же история, что была с удалённой заметкой.
    const оставшиеся = new Set(
      Object.entries(правки)
        .filter(([, phases]) => Array.isArray(phases) && phases.length > 0)
        .map(([programId]) => KEY.customPhases(programId)),
    )
    for (const key of logicalKeys(await storage.keys())) {
      if (key.startsWith('custom_phases_') && !оставшиеся.has(key)) await storage.remove(key)
    }
    // Списки ВБ — тем же правилом: что у сервера есть — кладём, чего нет — убираем
    const графики = answer.child.wakeSchedules ?? {}
    const живые = new Set<string>()
    for (const [programId, list] of Object.entries(графики)) {
      if (!Array.isArray(list) || list.length === 0) continue
      await setLong(storage, KEY.wakeSchedules(programId), JSON.stringify(list))
      живые.add(KEY.wakeSchedules(programId))
    }
    for (const key of logicalKeys(await storage.keys())) {
      if (key.startsWith('wake_schedules_') && !key.startsWith('wake_schedules_updated_at_') && !живые.has(key)) await storage.remove(key)
    }
  }

  // Записи о фазах: только дописываются, поэтому просто сливаем по номерам.
  if (answer.events.length > 0) {
    await mergeEvents(
      storage,
      answer.events.filter((e) => ВИДЫ.has(e.kind)).map<DayEvent>((e) => ({
        id: e.clientId,
        kind: e.kind,
        at: e.atMs,
        window: e.windowMs,
        ...(e.phaseIndex === null ? {} : { index: e.phaseIndex }),
        ...(e.title === null ? {} : { title: e.title }),
        ...(e.plannedMinutes === null ? {} : { planned: e.plannedMinutes }),
        ...(ПРИЧИНЫ.has(e.reason ?? '') ? { reason: e.reason as EndReason } : {}),
        ...(typeof e.grams === 'number' && e.grams > 0 ? { grams: e.grams } : {}),
        ...(e.manual === true ? { manual: true } : {}),
        ...(e.deleted === true ? { deleted: true } : {}),
        ...(typeof e.replacesMs === 'number' ? { replaces: e.replacesMs } : {}),
        ...(typeof e.place === 'string' && e.place !== '' ? { place: e.place } : {}),
        ...(typeof e.note === 'string' && e.note !== '' ? { note: e.note } : {}),
        ...(typeof e.wakeNote === 'string' && e.wakeNote !== '' ? { wakeNote: e.wakeNote } : {}),
      })),
    )
  }

  // История окон: раскладываем по месяцам, как её хранит устройство.
  const byMonth = new Map<string, Set<number>>()
  for (const key of logicalKeys(await storage.keys())) {
    if (!key.startsWith(WINDOWS_PREFIX)) continue
    const raw = await getLong(storage, key)
    const parsed = raw === null ? [] : (JSON.parse(raw) as unknown)
    if (Array.isArray(parsed)) byMonth.set(key, new Set(parsed.filter((v): v is number => typeof v === 'number')))
  }
  for (const w of answer.windows) {
    const key = KEY.windowsMonth(yearMonthOf(w.startedAtMs))
    const set = byMonth.get(key) ?? new Set<number>()
    set.add(w.startedAtMs)
    byMonth.set(key, set)
  }
  for (const [key, set] of byMonth) {
    await setLong(storage, key, JSON.stringify([...set].sort((a, b) => a - b)))
  }

  // Заметки: удалённые на сервере убираем и с устройства.
  const notesByMonth = new Map<string, Note[]>()
  const keep = answer.notes.filter((n) => !n.deleted)
  for (const n of keep) {
    const key = KEY.notesMonth(yearMonthOfEpochDay(n.day))
    const list = notesByMonth.get(key) ?? []
    list.push({ id: n.clientId, day: n.day, time: n.atMs, tag: n.tag, text: n.text })
    notesByMonth.set(key, list)
  }
  const touched = new Set(notesByMonth.keys())
  for (const key of logicalKeys(await storage.keys())) {
    if (key.startsWith(NOTES_PREFIX) && !touched.has(key) && answer.notes.length > 0) {
      await setLong(storage, key, '[]')
    }
  }
  for (const [key, list] of notesByMonth) {
    await setLong(storage, key, JSON.stringify(list.sort((a, b) => a.time - b.time)))
  }
}
